// React
import { Fragment, useCallback, useMemo } from "react"

// Router
import { useParams } from "@/lib/router"

// Queries
import {
	ProjectTypeEnum,
	StatsInformationByInterestPeriodType,
	useSingleProjectStatsInterestPeriodsQuery,
} from "@/api/graphql"

// UI
import { Card } from "@/components/Card"
import { Heading } from "@/components/Typography"
import { Tooltip } from "@/components/Tooltip"
import { DownloadSelector } from "@/components/DownloadSelector"
import { DownloadableFileType } from "@/components/DownloadSelector/types"

// Tables
import {
	useReactTable,
	flexRender,
	CellContext,
	createColumnHelper,
	getCoreRowModel,
} from "@/lib/table"
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"

// I18n
import { useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"

// Dates
import { DateTime as LuxonDateTime, useLocalizedDateTime } from "@/lib/dates"

// CSV
import { exportToCsv, getCsvFileName } from "@/lib/csv"

/**
 * ProjectInterestPeriodsTable
 * @returns
 */
export function ProjectInterestPeriodsTable() {
	// I18n
	const t = useTrans("dashboard")
	const { formatCurrency } = useLang()

	// Dates
	const DateTime = useLocalizedDateTime()

	// Router
	const params = useParams()

	// Fetch
	const { data, isLoading } = useSingleProjectStatsInterestPeriodsQuery({
		id: String(params.id),
	})
	const currentProject = data?.projects?.at(0)

	// Memo
	const stats = useMemo(() => {
		return (
			(data?.project_statistics
				?.stats_information_by_interest_period as StatsInformationByInterestPeriodType[]) ||
			[]
		)
	}, [data?.project_statistics?.stats_information_by_interest_period])

	// Table
	const columnHelper =
		createColumnHelper<StatsInformationByInterestPeriodType>()
	const columns = useMemo(
		() => [
			columnHelper.accessor("end_date", {
				id: "interest_period",
				header: () => (
					<TableHeading variant="static">
						{t("project:revenue.table.headers.interest_period")}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell className="font-medium">
						{DateTime.fromISO(info.getValue()).year}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("production_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.production_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{info.getValue()
							? Number(info.getValue()).toFixed(2)
							: "-"}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("energy_price", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.energy_price",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{info.getValue()
							? formatCurrency(Number(info.getValue()), {
									minimumFractionDigits: 4,
								})
							: "-"}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("return_on_investment", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.return_on_investment",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{Number(info.getValue()).toFixed(2)} %
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_production_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.expected_production_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{Number(info.getValue()).toFixed(2)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_solar_interest_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.expected_solar_interest_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{formatCurrency(Number(info.getValue()), {
							minimumFractionDigits: 2,
						})}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_interest_amount", {
				id: "expected_interest_amount",
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.expected_solar_interest_total",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string | null | undefined
					>,
				) => {
					const value = info.getValue()
					return (
						<TableDataCell>
							{value != null
								? formatCurrency(Number(value), {
										minimumFractionDigits: 2,
									})
								: "-"}
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor("total_actual_interest", {
				id: "total_actual_interest",
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.solar_interest_total",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string | null | undefined
					>,
				) => {
					const value = info.getValue()
					return (
						<TableDataCell className="font-medium">
							{value != null
								? formatCurrency(Number(value), {
										minimumFractionDigits: 2,
									})
								: "-"}
						</TableDataCell>
					)
				},
			}),
			columnHelper.accessor("expected_payment_per_share", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.expected_payment_per_share",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{formatCurrency(Number(info.getValue()), {
							minimumFractionDigits: 2,
						})}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("expected_return_on_investment", {
				header: () => (
					<TableHeading>
						{t(
							"dashboard:stats.project_stats.table.interest_periods.expected_return_on_investment",
						)}
					</TableHeading>
				),
				cell: (
					info: CellContext<
						StatsInformationByInterestPeriodType,
						string
					>,
				) => (
					<TableDataCell>
						{Number(info.getValue()).toFixed(2)} %
					</TableDataCell>
				),
			}),
		],
		[],
	)
	const table = useReactTable({
		columns,
		data: stats,
		getCoreRowModel: getCoreRowModel(),
		enableHiding: true,
		state: {
			// Only show these columns if we have a solarShares project. While
			// the project is still loading we don't know the type yet — assume
			// SolarShares so the loading skeleton matches the most common
			// layout instead of flickering extra columns in once data lands.
			columnVisibility: {
				energy_price:
					!currentProject ||
					currentProject.type === ProjectTypeEnum.SolarShares,
				production_per_share:
					!currentProject ||
					currentProject.type === ProjectTypeEnum.SolarShares,

				// TODO: Re-enable these once calcs are fixed
				return_on_investment: false,
				expected_production_per_share: false,
				expected_solar_interest_per_share: false,
				expected_payment_per_share: false,
				expected_return_on_investment: false,
			},
		},
	})

	// CSV column map — keyed by column id. The downloader iterates the
	// table's visible leaf columns and reads its label + value extractor
	// from here. Adding a new column? Add its id to this map.
	const csvColumns: Record<
		string,
		{
			label: string
			value: (row: StatsInformationByInterestPeriodType) => string
		}
	> = {
		interest_period: {
			label: t(
				"dashboard:stats.project_stats.table.interest_periods.period",
			),
			value: (row) =>
				row.end_date
					? DateTime.fromISO(row.end_date).toLocaleString(
							LuxonDateTime.DATE_SHORT,
						)
					: "-",
		},
		production_per_share: {
			label: t(
				"dashboard:stats.project_stats.table.interest_periods.production_per_share",
			),
			value: (row) =>
				row.production_per_share != null
					? Number(row.production_per_share).toFixed(2)
					: "-",
		},
		energy_price: {
			label: t(
				"dashboard:stats.project_stats.table.interest_periods.energy_price",
			),
			value: (row) =>
				row.energy_price ? Number(row.energy_price).toFixed(4) : "-",
		},
		expected_interest_amount: {
			label: t(
				"dashboard:stats.project_stats.table.interest_periods.expected_solar_interest_total",
			),
			value: (row) =>
				row.expected_interest_amount != null
					? Number(row.expected_interest_amount).toFixed(2)
					: "-",
		},
		total_actual_interest: {
			label: t(
				"dashboard:stats.project_stats.table.interest_periods.solar_interest_total",
			),
			value: (row) =>
				row.total_actual_interest != null
					? Number(row.total_actual_interest).toFixed(2)
					: "-",
		},
	}

	const handleDownload = useCallback(
		(type: DownloadableFileType) => {
			if (type !== DownloadableFileType.Csv) return

			const visibleColumns = table
				.getVisibleLeafColumns()
				.map((c) => csvColumns[c.id])
				.filter((c): c is NonNullable<typeof c> => c != null)

			const headers = visibleColumns.map((c) => c.label)
			const rows = table.getRowModel().rows.map((tableRow) => {
				const out: Record<string, string> = {}
				for (const c of visibleColumns) {
					out[c.label] = c.value(tableRow.original)
				}
				return out
			})

			const tableTitle = t(
				"dashboard:stats.project_stats.table.interest_periods.title",
			)
			const csvTitle = currentProject?.name
				? `${currentProject.name} - ${tableTitle}`
				: tableTitle

			exportToCsv({
				filename: getCsvFileName(csvTitle),
				data: rows,
				headers,
			})
		},
		[table, t, currentProject?.name],
	)

	// Data state
	return (
		<Card className="row mt-5">
			{!isLoading && (
				<div className="mb-4 flex items-center justify-between">
					<Heading as="h2" styleAs="h5" className="text-gray-600">
						{t(
							"dashboard:stats.project_stats.table.interest_periods.title",
						)}
					</Heading>
					<Tooltip
						content={t(
							"dashboard:stats.project_stats.table.interest_periods.download_tooltip",
						)}
					>
						<span>
							<DownloadSelector
								options={[
									{
										name: t("common:button.download.csv"),
										value: DownloadableFileType.Csv,
									},
								]}
								onDownload={handleDownload}
							/>
						</span>
					</Tooltip>
				</div>
			)}
			<Table
				className="min-w-[64rem] lg:min-w-0"
				data-testid="tablebody"
				isLoading={isLoading}
				headerGroups={table.getHeaderGroups()}
			>
				<TableHead>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRowCell key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								return (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								)
							})}
						</TableRowCell>
					))}
				</TableHead>

				<TableBody
					data-testid="tablebody-overview"
					data-pageindex={table.getState().pagination.pageIndex}
				>
					{table.getRowModel().rows.map((row) => {
						const isOdd = row.index % 2 === 0
						return (
							<Fragment key={row.id}>
								<TableRowCell isOdd={isOdd}>
									{row
										.getVisibleCells()
										.map((cell, index) => {
											return (
												<Fragment key={index}>
													{flexRender(
														cell.column.columnDef
															.cell,
														cell.getContext(),
													)}
												</Fragment>
											)
										})}
								</TableRowCell>
							</Fragment>
						)
					})}
				</TableBody>
			</Table>
		</Card>
	)
}
